import { isFunction } from 'lodash-es';
import { COLOR_SCHEME_DARK } from '@namespace/helpers';
import { Card, CardStatus } from '../api/bankCard/types';
import {
  CARD_HASH_SERVICE_KEYS,
  PAN_STATUS,
  PAN_STATUS_BY_CARD_STATUS,
  PAN_TYPE
} from '../constants';
import { getKeyByServiceId } from './getKeyByServiceId';

type Error = {
  code: string;
  [key: string]: unknown;
};

type ErrorCode = string | Error;

export const formatErrorMessage = (errorCode: ErrorCode): string =>
  `accountingMessages.${
    typeof errorCode === 'object' ? errorCode.code : errorCode
  }`;

interface GetErrorParams {
  errorCode?: ErrorCode;
  error?: Error;
}

export const getErrorMsg = ({
  errorCode,
  error = {} as Error
}: GetErrorParams = {}) => formatErrorMessage(errorCode || error);

export const getExtPaySysErrorMsg = (errorCode: string) =>
  `web.error.${errorCode}`;

type SuccessMessageFn = <Data>(data: Data) => string;

export const formatSuccessMessage = <Data>(
  data: Data,
  successMessage: string | SuccessMessageFn
) =>
  isFunction(successMessage)
    ? (successMessage as SuccessMessageFn)(data)
    : successMessage;

type IconItem = {
  'icon-dark'?: string;
  icon?: string;
};

export const iconPath = (item: IconItem, selectedColorMode: string) => {
  const path =
    selectedColorMode === COLOR_SCHEME_DARK ? item['icon-dark'] : item?.icon;
  return path || item?.icon || '';
};

export const isCardHashService = (sid: number) =>
  CARD_HASH_SERVICE_KEYS.includes(getKeyByServiceId(sid));

export const findCardStatus = (statusList: CardStatus) => {
  const statusKey = Object.keys(statusList).find(
    (key): key is keyof CardStatus =>
      statusList[key as keyof CardStatus] as boolean
  );
  return PAN_STATUS[
    PAN_STATUS_BY_CARD_STATUS[
      statusKey as keyof typeof PAN_STATUS_BY_CARD_STATUS
    ]
  ];
};

/* eslint-disable no-bitwise */
const cardHasDeposit = (cardStatus: number) =>
  Boolean(cardStatus & PAN_STATUS.FIRST_DEPOSIT);

const cardBlockedByRMT = (cardStatus: number) =>
  Boolean(cardStatus & PAN_STATUS.BLOCKED_BY_RMT);

const cardBlockedByClient = (cardStatus: number) =>
  Boolean(cardStatus & PAN_STATUS.BLOCKED_BY_CLIENT);

export const withdrawAllowed = (status: number, isAddedOnPayout?: boolean) =>
  (cardHasDeposit(status) || isAddedOnPayout) &&
  !cardBlockedByClient(status) &&
  !cardBlockedByRMT(status);

export const isWalletType = (
  type: (typeof PAN_TYPE)[keyof typeof PAN_TYPE] | undefined
) => type === PAN_TYPE.WALLET;
export const isWallet = (card: Card | null) => {
  const { type, expMonth = '' } = card || {};
  return (
    isWalletType(type) || (type === PAN_TYPE.PAN_TOKEN && !expMonth.trim())
  );
};
export const isPanType = (type: (typeof PAN_TYPE)[keyof typeof PAN_TYPE]) =>
  type === PAN_TYPE.PAN;

export const createApplePaySessionRequestPayload = (
  countryCode: string,
  currencyCode: string,
  amount: string
) => ({
  countryCode,
  currencyCode,
  supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
  merchantCapabilities: ['supports3DS'],
  total: {
    label: 'Favbet',
    type: 'final',
    amount: Number(amount).toFixed(2)
  }
});
